import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

interface SearchResult {
  id: string;
  type: 'product' | 'article' | 'documentation';
  title: string;
  description: string;
  url: string;
  image?: string;
  category: string;
  date?: string;
}

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('relevance');

  // 模拟搜索结果数据
  const mockResults: SearchResult[] = [
    {
      id: '1',
      type: 'product',
      title: 'Adobe PDF Library SDK',
      description: '借助可靠的 Adobe 技术、易用的 API 以及专业支持简化开发流程,从而更快速、更智能且更有把握地开展构建工作。支持 C++、.NET、.NET Framework 和 Java 的试用版。',
      url: '/#pdf-sdk',
      image: 'https://public.readdy.ai/ai/img_res/bef7dba97629db66ae5a7561ed718c3a.jpg',
      category: 'SDK产品',
      date: '2024-01-15'
    },
    {
      id: '2',
      type: 'product',
      title: 'Adobe PDF转换器',
      description: '高性能转换引擎,可在多种格式之间转换文档,同时保持质量和格式。支持批量转换、多格式支持、质量保持和云集成。',
      url: '/#pdf-converter',
      image: 'https://readdy.ai/api/search-image?query=Document%20conversion%20process%20visualization%20showing%20various%20file%20formats%20transforming%20into%20PDF%2C%20arrows%20indicating%20conversion%20flow%2C%20multiple%20document%20types%20like%20Word%20Excel%20PowerPoint%20converting%20to%20PDF%20format%2C%20clean%20professional%20interface%20with%20progress%20indicators&width=400&height=300&seq=search-converter&orientation=landscape',
      category: '转换工具',
      date: '2024-01-10'
    },
    {
      id: '3',
      type: 'product',
      title: 'PDF表单扩展',
      description: '为静态 XFA、动态 XFA 和 PDF AcroForms 添加数字文档支持。提供智能表单识别、数据验证、自动填充功能和表单分析。',
      url: '/#pdf-forms',
      image: 'https://readdy.ai/api/search-image?query=Interactive%20PDF%20form%20interface%20with%20fillable%20fields%20highlighted%2C%20smart%20form%20recognition%20technology%2C%20data%20extraction%20visualization%2C%20professional%20business%20forms%20with%20automated%20processing%20indicators%2C%20clean%20modern%20design%20with%20teal%20accents&width=400&height=300&seq=search-forms&orientation=landscape',
      category: '表单工具',
      date: '2024-01-05'
    },
    {
      id: '4',
      type: 'documentation',
      title: 'PDF SDK 开发者文档',
      description: '完整的 Adobe PDF Library SDK 开发者指南,包含 API 参考、代码示例、最佳实践和集成教程。帮助开发者快速上手并充分利用 SDK 的强大功能。',
      url: '/#services',
      category: '技术文档',
      date: '2024-01-20'
    },
    {
      id: '5',
      type: 'article',
      title: '如何使用 PDF SDK 进行批量处理',
      description: '学习如何使用 Adobe PDF Library SDK 高效处理大量 PDF 文档。本文介绍了批量转换、批量签名和批量提取的最佳实践。',
      url: '/#features',
      category: '技术文章',
      date: '2024-01-18'
    },
    {
      id: '6',
      type: 'documentation',
      title: 'PDF 转换 API 参考',
      description: '详细的 PDF 转换器 API 文档,涵盖所有支持的文件格式、转换选项和高级配置。包含完整的代码示例和错误处理指南。',
      url: '/#services',
      category: '技术文档',
      date: '2024-01-12'
    },
    {
      id: '7',
      type: 'article',
      title: 'PDF 表单自动化最佳实践',
      description: '探索 PDF 表单自动化的高级技巧,包括智能字段识别、数据验证规则和自动填充策略。提高表单处理效率的实用指南。',
      url: '/#features',
      category: '技术文章',
      date: '2024-01-08'
    },
    {
      id: '8',
      type: 'product',
      title: 'PDF 数字签名解决方案',
      description: '企业级 PDF 数字签名解决方案,支持多种签名标准和证书管理。确保文档的安全性、完整性和法律效力。',
      url: '/#pdf-sdk',
      image: 'https://readdy.ai/api/search-image?query=Digital%20signature%20technology%20visualization%2C%20secure%20document%20signing%20process%2C%20certificate%20authentication%20interface%2C%20professional%20business%20document%20with%20digital%20seal%2C%20teal%20and%20cyan%20security%20theme&width=400&height=300&seq=search-signature&orientation=landscape',
      category: '安全工具',
      date: '2024-01-03'
    }
  ];

  useEffect(() => {
    // 模拟搜索过滤
    if (query) {
      const filtered = mockResults.filter(result => 
        result.title.toLowerCase().includes(query.toLowerCase()) ||
        result.description.toLowerCase().includes(query.toLowerCase()) ||
        result.category.toLowerCase().includes(query.toLowerCase())
      );
      setResults(filtered);
    } else {
      setResults(mockResults);
    }
  }, [query]);

  const filteredResults = results.filter(result => {
    if (activeFilter === 'all') return true;
    return result.type === activeFilter;
  });

  const sortedResults = [...filteredResults].sort((a, b) => {
    if (sortBy === 'relevance') return 0;
    if (sortBy === 'date') {
      return new Date(b.date || '').getTime() - new Date(a.date || '').getTime();
    }
    if (sortBy === 'title') {
      return a.title.localeCompare(b.title);
    }
    return 0;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'product':
        return 'ri-product-hunt-line';
      case 'article':
        return 'ri-article-line';
      case 'documentation':
        return 'ri-book-line';
      default:
        return 'ri-file-line';
    }
  };

  const getTypeName = (type: string) => {
    switch (type) {
      case 'product':
        return '产品';
      case 'article':
        return '文章';
      case 'documentation':
        return '文档';
      default:
        return '其他';
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* 搜索头部 */}
          <div className="mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              搜索结果
            </h1>
            {query && (
              <p className="text-xl text-gray-600">
                关于 "<strong className="text-gray-900">{query}</strong>" 的搜索结果:找到 <strong className="text-teal-600">{sortedResults.length}</strong> 条结果
              </p>
            )}
          </div>

          {/* 过滤和排序栏 */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* 类型过滤 */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`px-5 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeFilter === 'all'
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-teal-50'
                  }`}
                >
                  全部
                </button>
                <button
                  onClick={() => setActiveFilter('product')}
                  className={`px-5 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeFilter === 'product'
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-teal-50'
                  }`}
                >
                  产品
                </button>
                <button
                  onClick={() => setActiveFilter('article')}
                  className={`px-5 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeFilter === 'article'
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-teal-50'
                  }`}
                >
                  文章
                </button>
                <button
                  onClick={() => setActiveFilter('documentation')}
                  className={`px-5 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeFilter === 'documentation'
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-teal-50'
                  }`}
                >
                  文档
                </button>
              </div>

              {/* 排序选择 */}
              <div className="flex items-center gap-3">
                <span className="text-gray-700 font-medium whitespace-nowrap">排序方式:</span>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-gray-700 font-medium cursor-pointer hover:border-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all"
                  >
                    <option value="relevance">相关性</option>
                    <option value="date">最新发布</option>
                    <option value="title">标题排序</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"></i>
                </div>
              </div>
            </div>
          </div>

          {/* 搜索结果列表 */}
          {sortedResults.length > 0 ? (
            <div className="space-y-6">
              {sortedResults.map((result) => (
                <article
                  key={result.id}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-teal-200 transition-all"
                >
                  <Link to={result.url} className="flex flex-col lg:flex-row cursor-pointer">
                    {result.image && (
                      <div className="lg:w-80 h-56 lg:h-auto flex-shrink-0">
                        <img
                          src={result.image}
                          alt={result.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    )}
                    <div className="flex-1 p-6 lg:p-8">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-700 rounded-full text-sm font-medium">
                          <i className={`${getTypeIcon(result.type)} text-base`}></i>
                          {getTypeName(result.type)}
                        </span>
                        <span className="text-gray-500 text-sm">{result.category}</span>
                        {result.date && (
                          <>
                            <span className="text-gray-300">•</span>
                            <span className="text-gray-500 text-sm">{result.date}</span>
                          </>
                        )}
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3 hover:text-teal-600 transition-colors">
                        {result.title}
                      </h2>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {result.description}
                      </p>
                      <div className="flex items-center text-teal-600 font-medium hover:text-teal-700 transition-colors">
                        <span className="whitespace-nowrap">查看详情</span>
                        <i className="ri-arrow-right-line ml-2"></i>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-24 h-24 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-search-line text-5xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">未找到相关结果</h3>
              <p className="text-gray-600 mb-8">
                抱歉,没有找到与 "{query}" 相关的内容。请尝试其他关键词。
              </p>
              <Link
                to="/"
                className="inline-block bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer shadow-lg"
              >
                返回首页
              </Link>
            </div>
          )}

          {/* 分页 */}
          {sortedResults.length > 0 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-teal-50 hover:border-teal-600 transition-colors cursor-pointer">
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold cursor-pointer shadow-lg">
                1
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-teal-50 hover:border-teal-600 transition-colors cursor-pointer">
                2
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-teal-50 hover:border-teal-600 transition-colors cursor-pointer">
                3
              </button>
              <span className="text-gray-500 px-2">...</span>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-teal-50 hover:border-teal-600 transition-colors cursor-pointer">
                10
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-teal-50 hover:border-teal-600 transition-colors cursor-pointer">
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}